import React, { useState, useEffect } from 'react';
import DOMPurify from 'dompurify';
import axios from 'axios';
import { toast } from 'sonner';
import { X, Mail, Clock, CheckSquare, GitBranch, Sparkles, Tag, Bell, Flag, Zap, Globe, Users, FileText, Target, Timer, Send, MessageSquareQuote, MessageCircle, AlertCircle, Loader2, CheckCircle, Save } from 'lucide-react';
import { Button } from '../ui/button';
import ConditionBuilder from './ConditionBuilder';
import EmailEditorModal from '../EmailEditorModal';
import TagInput from '../TagInput';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INPUT = "w-full px-3 py-2 text-sm border border-stone-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-stone-900/20";
const LABEL = "block text-xs font-medium text-stone-600 mb-1";

// ─── Per-Step Config Components ─────────────────────────────────────────────

const TIMEZONE_OPTIONS = [
    { value: '', label: 'Use campaign default' },
    { value: 'UTC', label: 'UTC' },
    { value: 'America/New_York', label: 'Eastern (New York)' },
    { value: 'America/Chicago', label: 'Central (Chicago)' },
    { value: 'America/Denver', label: 'Mountain (Denver)' },
    { value: 'America/Los_Angeles', label: 'Pacific (Los Angeles)' },
    { value: 'America/Sao_Paulo', label: 'São Paulo' },
    { value: 'Europe/London', label: 'London (GMT/BST)' },
    { value: 'Europe/Paris', label: 'Paris (CET)' },
    { value: 'Europe/Berlin', label: 'Berlin (CET)' },
    { value: 'Asia/Dubai', label: 'Dubai (GST)' },
    { value: 'Asia/Kolkata', label: 'India (IST)' },
    { value: 'Asia/Singapore', label: 'Singapore (SGT)' },
    { value: 'Asia/Manila', label: 'Manila (PHT)' },
    { value: 'Asia/Tokyo', label: 'Tokyo (JST)' },
    { value: 'Asia/Shanghai', label: 'China (CST)' },
    { value: 'Australia/Sydney', label: 'Sydney (AEST)' },
    { value: 'Pacific/Auckland', label: 'Auckland (NZST)' },
];

// Convert a HH:MM time from one timezone to the user's local timezone
function convertTimeToLocal(timeStr, fromTz) {
    try {
        const userTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
        if (fromTz === userTz) return null; // same tz, no need to show
        const [h, m] = timeStr.split(':').map(Number);
        // Use a fixed date to avoid DST edge cases
        const date = new Date();
        const srcDate = new Date(date.toLocaleString('en-US', { timeZone: fromTz }));
        const localDate = new Date(date.toLocaleString('en-US', { timeZone: userTz }));
        const offsetMs = localDate - srcDate;
        const result = new Date(2024, 0, 15, h, m);
        result.setTime(result.getTime() + offsetMs);
        return result.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
    } catch { return null; }
}

function getUserTzLabel() {
    try {
        const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
        return tz.replace(/_/g, ' ').split('/').pop();
    } catch { return 'local'; }
}

function EmailConfig({ config, onChange, onApply, campaignSettings = {}, campaignId, stepId, getAuthHeader }) {
    const [showEditor, setShowEditor] = useState(false);
    const [testEmail, setTestEmail] = useState('');
    const [sendingTest, setSendingTest] = useState(false);
    const [testSent, setTestSent] = useState(false);
    const useSendWindow = config.send_window_enabled || false;
    const campaignTz = campaignSettings.execution_timezone || 'UTC';
    const effectiveTz = config.send_timezone || campaignTz;
    const effectiveTzLabel = TIMEZONE_OPTIONS.find(o => o.value === effectiveTz)?.label || effectiveTz;

    const handleSendTest = async () => {
        if (!testEmail.trim() || sendingTest) return;
        if (!config.subject && !config.body) {
            toast.error('Add email subject and body first');
            return;
        }
        setSendingTest(true);
        setTestSent(false);
        try {
            const headers = getAuthHeader ? getAuthHeader() : {};
            // Send current in-editor content directly — no need to Apply + Save first
            await axios.post(`${API}/campaigns/${campaignId}/send-test`, {
                to_email: testEmail.trim(),
                step_id: stepId,
                subject: config.subject || '',
                body: config.body || '',
            }, { headers });
            setTestSent(true);
            toast.success(`Test email sent to ${testEmail.trim()}`);
            setTimeout(() => setTestSent(false), 5000);
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to send test email');
        } finally {
            setSendingTest(false);
        }
    };

    // Extract visible text from body HTML for preview (handles full HTML docs from block serializer)
    const bodyPreviewText = React.useMemo(() => {
        if (!config.body) return '';
        // Remove style/head/script tags and their contents before extracting text
        let html = config.body
            .replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '')
            .replace(/<head[^>]*>[\s\S]*?<\/head>/gi, '')
            .replace(/<script[^>]*>[\s\S]*?<\/script>/gi, '')
            .replace(/<!--[\s\S]*?-->/g, '')
            .replace(/<!\s*DOCTYPE[^>]*>/gi, '');
        const tmp = document.createElement('div');
        tmp.innerHTML = html;
        const text = (tmp.textContent || tmp.innerText || '').replace(/\s+/g, ' ').trim();
        return text.substring(0, 200);
    }, [config.body]);

    return (
        <div className="space-y-3">
            <div>
                <label className={LABEL}>Subject</label>
                <input value={config.subject || ''} onChange={e => onChange({ ...config, subject: e.target.value })}
                    placeholder="Email subject line..." className={INPUT} />
            </div>
            <div>
                <label className={LABEL}>Body Preview</label>
                {bodyPreviewText ? (
                    <div className="bg-stone-50 border border-stone-200 rounded-lg p-2 text-xs text-stone-600 max-h-20 overflow-hidden">
                        {bodyPreviewText}
                    </div>
                ) : (
                    <p className="text-xs text-stone-400">No email body yet</p>
                )}
                <button type="button" onClick={() => setShowEditor(true)}
                    className="mt-1.5 w-full px-3 py-2 text-xs font-medium bg-stone-900 text-white rounded-lg hover:bg-stone-800 transition-colors">
                    Edit Email
                </button>
            </div>
            <EmailEditorModal open={showEditor} onClose={() => setShowEditor(false)}
                subject={config.subject || ''} body={config.body || ''}
                emailBlocks={config.email_blocks || null}
                emailSettings={config.email_settings || null}
                onSave={({ subject, body, email_blocks, email_settings }) => {
                    const newConfig = { ...config, subject, body, email_blocks, email_settings };
                    onChange(newConfig);
                    // Auto-apply to parent so "Save Campaign" persists the email changes
                    if (onApply) onApply(newConfig);
                    setShowEditor(false);
                }} />

            {/* Send Window / Timezone */}
            <div className="border-t border-stone-100 pt-3">
                <label className="flex items-center gap-2 text-sm">
                    <input type="checkbox" checked={useSendWindow}
                        onChange={e => onChange({ ...config, send_window_enabled: e.target.checked })}
                        className="rounded border-stone-300" />
                    Schedule delivery window
                </label>
                {useSendWindow && (
                    <div className="mt-2 space-y-2 pl-6">
                        <div>
                            <label className={LABEL}>Timezone</label>
                            <select value={config.send_timezone || ''} onChange={e => onChange({ ...config, send_timezone: e.target.value })}
                                className={INPUT}>
                                {TIMEZONE_OPTIONS.map(tz => <option key={tz.value} value={tz.value}>{tz.label}</option>)}
                            </select>
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                            <div>
                                <label className={LABEL}>Earliest</label>
                                <input type="time" value={config.send_after_time || '08:00'}
                                    onChange={e => onChange({ ...config, send_after_time: e.target.value })}
                                    className={INPUT} />
                            </div>
                            <div>
                                <label className={LABEL}>Latest</label>
                                <input type="time" value={config.send_before_time || '18:00'}
                                    onChange={e => onChange({ ...config, send_before_time: e.target.value })}
                                    className={INPUT} />
                            </div>
                        </div>
                        <div>
                            <label className={LABEL}>Send on days</label>
                            <div className="flex gap-1 mt-1">
                                {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((day, idx) => {
                                    const days = config.send_days || [0, 1, 2, 3, 4]; // Mon-Fri default
                                    const active = days.includes(idx);
                                    return (
                                        <button key={day} type="button"
                                            onClick={() => {
                                                const updated = active ? days.filter(d => d !== idx) : [...days, idx].sort();
                                                onChange({ ...config, send_days: updated });
                                            }}
                                            className={`px-1.5 py-1 text-[10px] font-medium rounded ${
                                                active ? 'bg-stone-900 text-white' : 'bg-stone-100 text-stone-400'
                                            }`}>
                                            {day}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                        <div className="bg-stone-50 rounded-lg p-2 flex items-start gap-1.5">
                            <Globe className="w-3 h-3 text-stone-500 mt-0.5 flex-shrink-0" />
                            <div className="text-[10px] text-stone-600">
                                <p>
                                    Sends between <strong>{config.send_after_time || '08:00'}</strong> – <strong>{config.send_before_time || '18:00'}</strong> in <strong>{effectiveTzLabel}</strong>.
                                    {!config.send_timezone && <span className="text-stone-400"> (inherited from sequence settings)</span>}
                                </p>
                                {(() => {
                                    const localStart = convertTimeToLocal(config.send_after_time || '08:00', effectiveTz);
                                    const localEnd = convertTimeToLocal(config.send_before_time || '18:00', effectiveTz);
                                    return localStart && localEnd ? (
                                        <p className="text-stone-400 mt-0.5">
                                            That's <strong className="text-stone-500">{localStart}</strong> – <strong className="text-stone-500">{localEnd}</strong> your time ({getUserTzLabel()})
                                        </p>
                                    ) : null;
                                })()}
                            </div>
                        </div>
                    </div>
                )}
                {!useSendWindow && (
                    <p className="text-[10px] text-stone-400 mt-1 pl-6">Sends immediately when step is reached. Enable to control exact delivery time.</p>
                )}
            </div>

            <div className="border-t border-stone-100 pt-3 space-y-2">
                <label className="flex items-center gap-2 text-sm">
                    <input type="checkbox" checked={config.use_disc || false} onChange={e => onChange({ ...config, use_disc: e.target.checked })}
                        className="rounded border-stone-300" />
                    Personalize with DISC profile
                </label>
                <label className="flex items-center gap-2 text-sm">
                    <input type="checkbox" checked={config.use_voice || false} onChange={e => onChange({ ...config, use_voice: e.target.checked })}
                        className="rounded border-stone-300" />
                    Use sender's voice profile
                </label>
            </div>

            {/* Send Test Email */}
            {campaignId && stepId && (
                <div className="border-t border-stone-100 pt-3">
                    <label className={LABEL}>Send Test Email</label>
                    <div className="flex gap-1.5">
                        <input
                            type="email"
                            value={testEmail}
                            onChange={e => { setTestEmail(e.target.value); setTestSent(false); }}
                            onKeyDown={e => { if (e.key === 'Enter') handleSendTest(); }}
                            placeholder="you@example.com"
                            className="flex-1 px-2.5 py-1.5 text-xs border border-stone-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-stone-400"
                        />
                        <button
                            onClick={handleSendTest}
                            disabled={sendingTest || !testEmail.trim() || (!config.subject && !config.body)}
                            className="px-3 py-1.5 text-xs font-medium bg-stone-900 text-white rounded-lg hover:bg-stone-800 disabled:opacity-40 disabled:cursor-not-allowed transition-colors flex items-center gap-1.5"
                        >
                            {sendingTest ? <Loader2 className="w-3 h-3 animate-spin" /> :
                             testSent ? <CheckCircle className="w-3 h-3 text-green-400" /> :
                             <Send className="w-3 h-3" />}
                            {sendingTest ? 'Sending...' : testSent ? 'Sent!' : 'Test'}
                        </button>
                    </div>
                    <p className="text-[10px] text-stone-400 mt-1">
                        Sends with [TEST] prefix. Merge fields use your profile as sample data.
                    </p>
                </div>
            )}
        </div>
    );
}

function WaitConfig({ config, onChange }) {
    return (
        <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
                <div>
                    <label className={LABEL}>Days</label>
                    <input type="number" min={0} value={config.duration_days || 0}
                        onChange={e => onChange({ ...config, duration_days: parseInt(e.target.value) || 0 })} className={INPUT} />
                </div>
                <div>
                    <label className={LABEL}>Hours</label>
                    <input type="number" min={0} max={23} value={config.duration_hours || 0}
                        onChange={e => onChange({ ...config, duration_hours: parseInt(e.target.value) || 0 })} className={INPUT} />
                </div>
            </div>
        </div>
    );
}

function IfElseConfig({ config, onChange, campaignSettings = {} }) {
    const campaignTz = campaignSettings.execution_timezone || 'UTC';
    const effectiveTz = config.timezone || campaignTz;
    const tzLabel = TIMEZONE_OPTIONS.find(o => o.value === effectiveTz)?.label || effectiveTz;
    return (
        <div className="space-y-3">
            <p className="text-xs text-stone-500">Define conditions. Contacts matching will take the "Yes" path, others take "No".</p>
            <ConditionBuilder
                value={config.condition_group || { logic: 'and', conditions: [] }}
                onChange={cg => onChange({ ...config, condition_group: cg })}
                campaignTimezone={tzLabel}
            />
            {/* Timezone for condition evaluation */}
            <div className="border-t border-stone-100 pt-2">
                <label className={LABEL}>Timezone</label>
                <select value={config.timezone || ''} onChange={e => onChange({ ...config, timezone: e.target.value })}
                    className={INPUT}>
                    <option value="">Use workflow default ({TIMEZONE_OPTIONS.find(o => o.value === campaignTz)?.label || campaignTz})</option>
                    {TIMEZONE_OPTIONS.filter(o => o.value).map(tz => <option key={tz.value} value={tz.value}>{tz.label}</option>)}
                </select>
                <p className="text-[10px] text-stone-400 mt-1">
                    Date/time conditions in this branch are evaluated in <strong className="text-stone-500">{tzLabel}</strong>
                    {!config.timezone && <span> (inherited from workflow settings)</span>}
                </p>
            </div>
        </div>
    );
}

function ABSplitConfig({ config, onChange }) {
    return (
        <div className="space-y-3">
            <label className={LABEL}>Split Percentage (Path A)</label>
            <div className="flex items-center gap-3">
                <input type="range" min={10} max={90} value={config.percentage || 50}
                    onChange={e => onChange({ ...config, percentage: parseInt(e.target.value) })}
                    className="flex-1" />
                <span className="text-sm font-medium text-stone-700 w-16 text-center">
                    {config.percentage || 50}% / {100 - (config.percentage || 50)}%
                </span>
            </div>
            <div className="flex justify-between text-xs text-stone-400">
                <span>Path A</span>
                <span>Path B</span>
            </div>
        </div>
    );
}

function TagUpdateConfig({ config, onChange }) {
    return (
        <div className="space-y-3">
            <div>
                <label className={LABEL}>Action</label>
                <select value={config.action || 'add'} onChange={e => onChange({ ...config, action: e.target.value })} className={INPUT}>
                    <option value="add">Add Tag</option>
                    <option value="remove">Remove Tag</option>
                </select>
            </div>
            <div>
                <label className={LABEL}>Tag Name</label>
                <TagInput
                    singleSelect
                    singleValue={config.tag || ''}
                    onSingleChange={tag => onChange({ ...config, tag })}
                    placeholder="Search or create a tag..."
                />
            </div>
        </div>
    );
}

function TaskConfig({ config, onChange }) {
    const subtype = config.task_subtype || 'general';
    const showAIPrompt = ['email', 'linkedin', 'call'].includes(subtype);

    return (
        <div className="space-y-3">
            <div>
                <label className={LABEL}>Task Type</label>
                <select value={subtype} onChange={e => onChange({ ...config, task_subtype: e.target.value })} className={INPUT}>
                    <option value="general">General Task</option>
                    <option value="email">Send Email (Human)</option>
                    <option value="linkedin">Send LinkedIn Message</option>
                    <option value="call">Make Phone Call</option>
                </select>
                {showAIPrompt && (
                    <p className="text-[10px] text-stone-400 mt-1">AI will pre-write content using contact DISC, voice profile, and conversation history. Campaign pauses until task is completed.</p>
                )}
            </div>
            <div>
                <label className={LABEL}>Task Title</label>
                <input value={config.title || ''} onChange={e => onChange({ ...config, title: e.target.value })}
                    placeholder="Follow up call..." className={INPUT} />
            </div>
            <div>
                <label className={LABEL}>Description</label>
                <textarea value={config.description || ''} onChange={e => onChange({ ...config, description: e.target.value })}
                    rows={2} className={INPUT} />
            </div>
            {showAIPrompt && (
                <div>
                    <label className={LABEL}>AI Writing Instructions (Optional)</label>
                    <textarea
                        value={config.ai_prompt || ''}
                        onChange={e => onChange({ ...config, ai_prompt: e.target.value })}
                        rows={2}
                        placeholder="e.g., Follow up on our demo call, suggest scheduling a technical deep-dive next week"
                        className={INPUT}
                    />
                </div>
            )}
            <div>
                <label className={LABEL}>Priority</label>
                <select value={config.priority || 'medium'} onChange={e => onChange({ ...config, priority: e.target.value })} className={INPUT}>
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                </select>
            </div>
        </div>
    );
}

function NotificationConfig({ config, onChange }) {
    return (
        <div className="space-y-3">
            <div>
                <label className={LABEL}>Message</label>
                <textarea value={config.message || ''} onChange={e => onChange({ ...config, message: e.target.value })}
                    rows={2} placeholder="Notification message..." className={INPUT} />
            </div>
            <div>
                <label className={LABEL}>Notify</label>
                <select value={config.notify_user || 'owner'} onChange={e => onChange({ ...config, notify_user: e.target.value })} className={INPUT}>
                    <option value="owner">Contact Owner</option>
                </select>
            </div>
        </div>
    );
}

function EndConfig({ config, onChange }) {
    return (
        <div>
            <label className={LABEL}>Outcome</label>
            <select value={config.outcome || 'completed'} onChange={e => onChange({ ...config, outcome: e.target.value })} className={INPUT}>
                <option value="completed">Completed</option>
                <option value="converted">Converted</option>
                <option value="lost">Lost</option>
                <option value="unresponsive">Unresponsive</option>
            </select>
        </div>
    );
}

function DealStageConfig({ config, onChange }) {
    return (
        <div>
            <label className={LABEL}>Move to Stage</label>
            <select value={config.stage || ''} onChange={e => onChange({ ...config, stage: e.target.value })} className={INPUT}>
                <option value="">Select stage...</option>
                <option value="Prospect">Prospect</option>
                <option value="Qualified">Qualified</option>
                <option value="Proposal Sent">Proposal Sent</option>
                <option value="Negotiation">Negotiation</option>
                <option value="Closed Won">Closed Won</option>
                <option value="Closed Lost">Closed Lost</option>
            </select>
        </div>
    );
}

function WaitForEventConfig({ config, onChange }) {
    const [forms, setForms] = useState([]);

    useEffect(() => {
        const token = localStorage.getItem('crm_token');
        if (token) {
            fetch(`${process.env.REACT_APP_BACKEND_URL}/api/forms`, {
                headers: { Authorization: `Bearer ${token}` },
            })
                .then(r => r.ok ? r.json() : [])
                .then(setForms)
                .catch(() => {});
        }
    }, []);

    return (
        <div className="space-y-3">
            <div>
                <label className={LABEL}>Wait for Event</label>
                <select value={config.event_type || ''} onChange={e => onChange({ ...config, event_type: e.target.value })} className={INPUT}>
                    <option value="">Select event...</option>
                    <option value="email_opened">Email Opened</option>
                    <option value="email_clicked">Email Clicked</option>
                    <option value="email_replied">Email Replied</option>
                    <option value="form_submitted">Form Submitted</option>
                    <option value="booking_confirmed">Appointment Booked</option>
                    <option value="tag_added">Tag Added</option>
                    <option value="deal_stage_changed">Deal Stage Changed</option>
                    <option value="webhook_received">Webhook Received</option>
                </select>
            </div>
            {config.event_type === 'form_submitted' && forms.length > 0 && (
                <div>
                    <label className={LABEL}>Specific Form (optional)</label>
                    <select value={config.form_id || ''} onChange={e => onChange({ ...config, form_id: e.target.value || undefined })} className={INPUT}>
                        <option value="">Any form</option>
                        {forms.map(f => <option key={f.form_id} value={f.form_id}>{f.name}</option>)}
                    </select>
                </div>
            )}
            {config.event_type === 'form_submitted' && forms.length > 0 && config.form_id && (
                <div className="bg-blue-50 rounded-lg p-2">
                    <p className="text-[10px] text-blue-600">
                        Form URL: <code className="text-[10px]">https://app.nurturecrm.ai/form/{forms.find(f => f.form_id === config.form_id)?.slug || '...'}</code>
                    </p>
                </div>
            )}
            <div>
                <label className={LABEL}>Timeout (hours)</label>
                <input type="number" min={1} value={config.timeout_hours || 72}
                    onChange={e => onChange({ ...config, timeout_hours: parseInt(e.target.value) || 72 })} className={INPUT} />
                <p className="text-xs text-stone-400 mt-1">If event doesn't occur within this time, continue to next step</p>
            </div>
        </div>
    );
}

function DelayUntilConfig({ config, onChange, campaignSettings = {} }) {
    const campaignTz = campaignSettings.execution_timezone || 'UTC';
    const effectiveTz = config.timezone || campaignTz;
    const tzLabel = TIMEZONE_OPTIONS.find(o => o.value === effectiveTz)?.label || effectiveTz;

    return (
        <div className="space-y-3">
            <div>
                <label className={LABEL}>Mode</label>
                <select value={config.mode || 'fixed'} onChange={e => onChange({ ...config, mode: e.target.value })} className={INPUT}>
                    <option value="fixed">Fixed Date/Time</option>
                    <option value="field_offset">Contact Field + Offset</option>
                </select>
            </div>
            {config.mode === 'field_offset' ? (
                <>
                    <div>
                        <label className={LABEL}>Date Field</label>
                        <input value={config.date_field || ''} onChange={e => onChange({ ...config, date_field: e.target.value })}
                            placeholder="e.g. contact.custom_fields.event_date" className={INPUT} />
                    </div>
                    <div>
                        <label className={LABEL}>Offset (hours, negative = before)</label>
                        <input type="number" value={config.offset_hours || -24}
                            onChange={e => onChange({ ...config, offset_hours: parseInt(e.target.value) || 0 })} className={INPUT} />
                    </div>
                </>
            ) : (
                <div>
                    <label className={LABEL}>Target Date/Time</label>
                    <input type="datetime-local" value={config.target_datetime || ''}
                        onChange={e => onChange({ ...config, target_datetime: e.target.value })} className={INPUT} />
                    <div className="mt-1.5 space-y-0.5 text-[10px] text-stone-500">
                        <div className="flex items-center gap-1.5">
                            <Globe className="w-3 h-3" />
                            <span>Interpreted as <strong>{tzLabel}</strong></span>
                        </div>
                        {config.target_datetime && (() => {
                            try {
                                const userTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
                                if (effectiveTz === userTz) return null;
                                const [datePart, timePart] = config.target_datetime.split('T');
                                if (!timePart) return null;
                                // Create date in the campaign timezone and show in user's tz
                                const fakeDate = new Date(config.target_datetime);
                                const srcNow = new Date(new Date().toLocaleString('en-US', { timeZone: effectiveTz }));
                                const localNow = new Date(new Date().toLocaleString('en-US', { timeZone: userTz }));
                                const offsetMs = localNow - srcNow;
                                const localDate = new Date(fakeDate.getTime() + offsetMs);
                                const localStr = localDate.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true });
                                return (
                                    <div className="flex items-center gap-1.5 text-stone-400">
                                        <Clock className="w-3 h-3" />
                                        <span>Your time ({getUserTzLabel()}): <strong className="text-stone-500">{localStr}</strong></span>
                                    </div>
                                );
                            } catch { return null; }
                        })()}
                    </div>
                </div>
            )}
            {/* Per-step timezone override */}
            <div className="border-t border-stone-100 pt-2">
                <label className={LABEL}>Timezone (override)</label>
                <select value={config.timezone || ''} onChange={e => onChange({ ...config, timezone: e.target.value })}
                    className={INPUT}>
                    <option value="">Use sequence default ({TIMEZONE_OPTIONS.find(o => o.value === campaignTz)?.label || campaignTz})</option>
                    {TIMEZONE_OPTIONS.filter(o => o.value).map(tz => <option key={tz.value} value={tz.value}>{tz.label}</option>)}
                </select>
            </div>
        </div>
    );
}

function WebhookCallConfig({ config, onChange }) {
    return (
        <div className="space-y-3">
            <div>
                <label className={LABEL}>URL</label>
                <input value={config.url || ''} onChange={e => onChange({ ...config, url: e.target.value })}
                    placeholder="https://api.example.com/webhook" className={INPUT} />
            </div>
            <div>
                <label className={LABEL}>Method</label>
                <select value={config.method || 'POST'} onChange={e => onChange({ ...config, method: e.target.value })} className={INPUT}>
                    <option value="POST">POST</option>
                    <option value="GET">GET</option>
                    <option value="PUT">PUT</option>
                </select>
            </div>
            <div>
                <label className={LABEL}>Payload (JSON)</label>
                <textarea value={config.body || ''} onChange={e => onChange({ ...config, body: e.target.value })}
                    rows={3} placeholder='{"contact_id": "{{contact.contact_id}}"}' className={`${INPUT} font-mono text-xs`} />
            </div>
        </div>
    );
}

function TestimonialRequestConfig({ config, onChange }) {
    const [requests, setRequests] = useState([]);
    useEffect(() => {
        const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
        const token = localStorage.getItem('crm_token');
        if (token) {
            fetch(`${API}/testimonials`, { headers: { Authorization: `Bearer ${token}` } })
                .then(r => r.json()).then(d => setRequests(Array.isArray(d) ? d : d.data || [])).catch(() => {});
        }
    }, []);
    return (
        <div className="space-y-3">
            <div>
                <label className={LABEL}>Testimonial Request</label>
                <select value={config.testimonial_request_id || ''} onChange={e => onChange({ ...config, testimonial_request_id: e.target.value })} className={INPUT}>
                    <option value="">Select a testimonial request...</option>
                    {requests.map(r => <option key={r.request_id} value={r.request_id}>{r.name}</option>)}
                </select>
            </div>
            <div>
                <label className={LABEL}>Email Subject (optional override)</label>
                <input value={config.email_subject || ''} onChange={e => onChange({ ...config, email_subject: e.target.value })}
                    placeholder="Leave blank for default" className={INPUT} />
            </div>
            <div>
                <label className={LABEL}>Email Body (optional override)</label>
                <textarea value={config.email_body || ''} onChange={e => onChange({ ...config, email_body: e.target.value })}
                    rows={2} placeholder="Leave blank for default" className={INPUT} />
            </div>
        </div>
    );
}

function GoalConfig({ config, onChange, campaignSettings = {} }) {
    const campaignTz = campaignSettings.execution_timezone || 'UTC';
    const tzLabel = TIMEZONE_OPTIONS.find(o => o.value === campaignTz)?.label || campaignTz;
    return (
        <div className="space-y-3">
            <p className="text-xs text-stone-500">When this condition is met, the contact exits the workflow immediately regardless of their current position.</p>
            <ConditionBuilder
                value={config.condition_group || { logic: 'and', conditions: [] }}
                onChange={cg => onChange({ ...config, condition_group: cg })}
                campaignTimezone={tzLabel}
            />
        </div>
    );
}

function UpdateFieldConfig({ config, onChange }) {
    return (
        <div className="space-y-3">
            <div>
                <label className={LABEL}>Field</label>
                <input value={config.field || ''} onChange={e => onChange({ ...config, field: e.target.value })}
                    placeholder="e.g. contact.seniority" className={INPUT} />
            </div>
            <div>
                <label className={LABEL}>Value</label>
                <input value={config.value || ''} onChange={e => onChange({ ...config, value: e.target.value })}
                    placeholder="New value..." className={INPUT} />
            </div>
        </div>
    );
}

function WhatsAppConfig({ config, onChange }) {
    return (
        <div className="space-y-3">
            <div>
                <label className={LABEL}>Template Name</label>
                <input value={config.template_name || ''} onChange={e => onChange({ ...config, template_name: e.target.value })}
                    placeholder="e.g. hello_world" className={INPUT} />
                <p className="text-[10px] text-stone-400 mt-1">Required for automated messages (sent outside service window)</p>
            </div>
            <div>
                <label className={LABEL}>Message Body (freeform fallback)</label>
                <textarea value={config.message_body || ''} onChange={e => onChange({ ...config, message_body: e.target.value })}
                    placeholder="Fallback message if within service window..." className={INPUT} rows={3} />
            </div>
        </div>
    );
}

function AgenticEmailConfig({ config, onChange }) {
    return (
        <div className="space-y-3">
            <div className="bg-violet-50 border border-violet-200 rounded-lg p-2.5">
                <p className="text-[11px] text-violet-700">
                    <strong>AI Personalized Email</strong> — The AI will write a unique email for each contact using the sender's voice profile, the contact's DISC profile, and communication history. Requires human review before sending.
                </p>
            </div>
            <div>
                <label className={LABEL}>Objective / Prompt</label>
                <textarea value={config.objective || ''} onChange={e => onChange({ ...config, objective: e.target.value })}
                    placeholder="e.g. Write a follow-up email after our demo call, highlighting the ROI we discussed..."
                    className={INPUT} rows={3} />
                <p className="text-[10px] text-stone-400 mt-1">Tell the AI what this email should accomplish</p>
            </div>
            <div>
                <label className={LABEL}>Tone Override (optional)</label>
                <select value={config.tone_override || ''} onChange={e => onChange({ ...config, tone_override: e.target.value })}
                    className={INPUT}>
                    <option value="">Use sender's natural voice</option>
                    <option value="formal">More Formal</option>
                    <option value="casual">More Casual</option>
                    <option value="urgent">Urgent</option>
                    <option value="warm">Warm & Friendly</option>
                    <option value="concise">Concise & Direct</option>
                </select>
            </div>
            <div>
                <label className={LABEL}>Email Length</label>
                <select value={config.max_length || 'medium'} onChange={e => onChange({ ...config, max_length: e.target.value })}
                    className={INPUT}>
                    <option value="short">Short (~100 words)</option>
                    <option value="medium">Medium (~200 words)</option>
                    <option value="long">Long (~350 words)</option>
                </select>
            </div>
            <div>
                <label className={LABEL}>Call-to-Action Type</label>
                <select value={config.cta_type || 'reply'} onChange={e => onChange({ ...config, cta_type: e.target.value })}
                    className={INPUT}>
                    <option value="reply">Encourage Reply</option>
                    <option value="meeting">Schedule Meeting</option>
                    <option value="link_click">Click a Link</option>
                    <option value="custom">Custom</option>
                </select>
            </div>
            {config.cta_type === 'custom' && (
                <div>
                    <label className={LABEL}>Custom CTA</label>
                    <input value={config.cta_custom || ''} onChange={e => onChange({ ...config, cta_custom: e.target.value })}
                        placeholder="Describe the desired call to action..." className={INPUT} />
                </div>
            )}
            <div className="flex items-center gap-2">
                <input type="checkbox" id="include_deal" checked={config.include_deal_context !== false}
                    onChange={e => onChange({ ...config, include_deal_context: e.target.checked })}
                    className="rounded border-stone-300" />
                <label htmlFor="include_deal" className="text-xs text-stone-600">Include deal context</label>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-2.5 flex items-start gap-2">
                <Sparkles className="w-3.5 h-3.5 text-amber-500 mt-0.5 flex-shrink-0" />
                <p className="text-[10px] text-amber-700">
                    All AI-generated emails require human review before sending. Drafts appear in the <strong>AI Reviews</strong> queue.
                </p>
            </div>
        </div>
    );
}

// ─── Config Registry ────────────────────────────────────────────────────────

const CONFIG_COMPONENTS = {
    email: EmailConfig,
    wait: WaitConfig,
    if_else: IfElseConfig,
    branch: IfElseConfig,
    ab_split: ABSplitConfig,
    tag_update: TagUpdateConfig,
    task: TaskConfig,
    notification: NotificationConfig,
    end: EndConfig,
    deal_stage_update: DealStageConfig,
    wait_for_event: WaitForEventConfig,
    delay_until: DelayUntilConfig,
    webhook_call: WebhookCallConfig,
    goal: GoalConfig,
    update_field: UpdateFieldConfig,
    request_testimonial: TestimonialRequestConfig,
    send_whatsapp: WhatsAppConfig,
    agentic_email: AgenticEmailConfig,
};

const STEP_ICONS = {
    email: Mail, wait: Clock, if_else: GitBranch, branch: GitBranch, ab_split: Zap,
    tag_update: Tag, task: CheckSquare, notification: Bell, end: Flag,
    deal_stage_update: Target, wait_for_event: Timer, delay_until: Timer,
    webhook_call: Globe, goal: Target, update_field: FileText,
    ai_branch: Sparkles, assign_user: Users, add_to_workflow: Send,
    remove_from_workflow: X, smart_delay: Clock, send_sms: Send,
    request_testimonial: MessageSquareQuote,
    send_whatsapp: MessageCircle,
    agentic_email: Sparkles,
};

// ─── Main Panel ─────────────────────────────────────────────────────────────

export default function StepConfigPanel({ step, onUpdate, onClose, campaignSettings = {}, campaignId, getAuthHeader }) {
    const [config, setConfig] = useState(step?.config || {});
    const [applied, setApplied] = useState(false);

    // FR-013 fix — re-sync local config state when the parent's step.config
    // reference changes, not just on step_id change. The Insert-template
    // flow (FR-013) replaces node.data.config from outside this panel; the
    // old `[step?.step_id]` dep meant Insert appeared to no-op (stale local
    // state still showed) AND Apply Changes wrote the stale state back over
    // the freshly-inserted body, blanking the email. (Reported by Peleg.)
    // Spreads in the parent's metrics-merge useEffect preserve config by
    // reference, so this dep only fires on actual config replacements
    // (Insert, AI Chat workflow updates) — not every metrics refresh.
    useEffect(() => {
        setConfig(step?.config || {});
    }, [step?.step_id, step?.config]);

    if (!step) return null;

    const ConfigComponent = CONFIG_COMPONENTS[step.step_type];
    const Icon = STEP_ICONS[step.step_type] || FileText;

    const handleSave = () => {
        onUpdate({ ...step, config, label: step.label });
        setApplied(true);
        toast.success('Changes applied to step');
        setTimeout(() => setApplied(false), 2500);
    };

    return (
        <div className="w-72 bg-white border-l border-stone-200 flex flex-col h-full">
            {/* Header + Apply button (always visible at top) */}
            <div className="px-4 py-3 border-b border-stone-200 flex-shrink-0">
                <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                        <Icon className="w-4 h-4 text-stone-500" />
                        <span className="text-sm font-semibold text-stone-900 capitalize">{step.step_type.replace(/_/g, ' ')}</span>
                    </div>
                    <button onClick={onClose} className="p-1 text-stone-400 hover:text-stone-600">
                        <X className="w-4 h-4" />
                    </button>
                </div>
                <Button size="sm" onClick={handleSave} className={`w-full transition-all ${applied ? 'bg-green-600 hover:bg-green-600' : ''}`}>
                    {applied ? (
                        <><CheckCircle className="w-3.5 h-3.5 mr-1" /> Applied!</>
                    ) : (
                        <><Save className="w-3.5 h-3.5 mr-1" /> Apply Changes</>
                    )}
                </Button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
                {/* Step Label */}
                <div>
                    <label className={LABEL}>Step Label</label>
                    <input value={step.label || ''} onChange={e => onUpdate({ ...step, label: e.target.value })}
                        placeholder="Step name..." className={INPUT} />
                </div>

                {/* Step-specific config */}
                {ConfigComponent ? (
                    <ConfigComponent config={config} onChange={setConfig} campaignSettings={campaignSettings}
                        campaignId={campaignId} stepId={step?.step_id} getAuthHeader={getAuthHeader}
                        onApply={(newConfig) => { setConfig(newConfig); onUpdate({ ...step, config: newConfig }); }} />
                ) : (
                    <p className="text-xs text-stone-400 italic">No configuration needed for this step type.</p>
                )}
            </div>
        </div>
    );
}
